import type { ReactNode } from 'react';
import { Trans } from 'react-i18next';
import formatCarbonIntensity from '/shared/functions/formatCarbonIntensity';
type InfoCardBodyItem =
  | ReactNode
  | {
      bold?: boolean;
      key: string;
      text: ReactNode;
    };
type InfoCardBodyCallbackProps = {
  body: InfoCardBodyItem[];
  carbonIntensityTotal: number | null;
};
export const introCards = [
  {
    // @i18n: What is 'carbon intensity'?
    title: <Trans i18nKey="business:ReducePage.intro.cards.card1.title" />,
    body: [
      // @i18n: It is a way of standardizing the intensity of your operational carbon emissions per[...]
      <Trans i18nKey="business:ReducePage.intro.cards.card1.para1" />,
      // @i18n: This means that it can adjust with your business as you grow and expand your operations.
      <Trans i18nKey="business:ReducePage.intro.cards.card1.para2" />,
    ],
  },
  {
    // @i18n: How we calculate it
    title: <Trans i18nKey="business:ReducePage.intro.cards.card2.title" />,
    body: [
      // @i18n: Your total carbon emissions are divided by the value you entered for your chosen[...]
      <Trans i18nKey="business:ReducePage.intro.cards.card2.para1" />,
      // @i18n: This gives an estimated carbon intensity, expressed as tCO₂e per unit.
      <Trans i18nKey="business:ReducePage.intro.cards.card2.para2" />,
    ],
  },
  {
    // @i18n: How it works
    title: <Trans i18nKey="business:ReducePage.intro.cards.card3.title" />,
    body: [
      // @i18n: Your carbon intensity will usually be displayed as a decimal number.
      <Trans i18nKey="business:ReducePage.intro.cards.card3.para1" />,
      // @i18n: The closer it is to 0, the more you’re reducing your emissions.
      <Trans i18nKey="business:ReducePage.intro.cards.card3.para2" />,
    ],
  },
  {
    title: (
      // @i18n: What does 'baseline carbon intensity' mean?
      <Trans i18nKey="business:ReducePage.intro.cards.card4.title" />
    ),
    body: [
      // @i18n: It is a record of the carbon intensity calculated during your business's first carbon[...]
      <Trans i18nKey="business:ReducePage.intro.cards.card4.para1" />,
    ],
  },
];
export const infoCards = [
  {
    id: 'castrol-minimum-reduction',
    // @i18n: Minimum reduction target
    title: <Trans i18nKey="business:ReducePage.reductionBar.cards.reductionTarget.title" />,
    body: [
      // @i18n: To achieve and maintain your carbon neutral status, your carbon intensity reduction[...]
      <Trans i18nKey="business:ReducePage.reductionBar.cards.reductionTarget.body" />,
      // @i18n: This reduction is cumulative across periods – e.g. Period 1: 5% reduction when[...]
      <Trans i18nKey="business:ReducePage.reductionBar.cards.reductionTarget.body2" />,
    ],
  },
  {
    id: 'castrol-carbon-intensity',
    // @i18n: What does ‘baseline carbon intensity’ mean?
    title: <Trans i18nKey="business:ReducePage.reductionBar.cards.carbonIntensity.title" />,
    body: [
      // @i18n: Baseline carbon intensity is a record of the carbon intensity calculated during your[...]
      <Trans i18nKey="business:ReducePage.reductionBar.cards.carbonIntensity.body" />,
      // @i18n: It is the reference point against which all reduction targets will be measured, and is[...]
      <Trans i18nKey="business:ReducePage.reductionBar.cards.carbonIntensity.body2" />,
    ],
    bodyCallback: ({ body, carbonIntensityTotal }: InfoCardBodyCallbackProps) => {
      const newBody = body;
      const carbonIntensity = formatCarbonIntensity(carbonIntensityTotal);
      if (
        !newBody.find((item) => {
          return typeof item === 'object' && item !== null && 'key' in item && item.key === 'new';
        })
      ) {
        newBody.push({
          bold: true,
          key: 'new',
          text: (
            // @i18n: Your baseline carbon intensity is: 2.5 tCO₂e per unit tCO₂e/m²
            <Trans
              i18nKey="business:ReducePage.reductionBar.cards.carbonIntensity.body3"
              values={{
                carbonIntensity,
              }}
            />
          ),
        });
      }
      return newBody;
    },
  },
];
export const reductionCards = [
  {
    // @i18n: What does ‘baseline carbon intensity’ mean?
    title: <Trans i18nKey="business:ReducePage.projections.cards.card1.title" />,
    // @i18n: It is a record of the carbon intensity calculated during your business's first carbon[...]
    body: [<Trans i18nKey="business:ReducePage.projections.cards.card1.body1" />],
  },
  {
    // @i18n: What does ‘reporting period’ mean?
    title: <Trans i18nKey="business:ReducePage.projections.cards.card2.title" />,
    body: [
      // @i18n: Your business's carbon review reporting period does not have any correlation with[...]
      <Trans i18nKey="business:ReducePage.projections.cards.card2.body1" />,
      // @i18n: __
      <Trans i18nKey="business:ReducePage.projections.cards.card2.body2" />,
    ],
  },
  {
    // @i18n: What does ‘next period’ mean?
    title: <Trans i18nKey="business:ReducePage.projections.cards.card3.title" />,
    body: [
      // @i18n: The next period is the reporting period for which you have been setting reduction[...]
      <Trans i18nKey="business:ReducePage.projections.cards.card3.body1" />,
      // @i18n: This represents the next period in your business's carbon reduction journey.
      <Trans i18nKey="business:ReducePage.projections.cards.card3.body2" />,
    ],
  },
  {
    // @i18n: What does ‘future periods’ mean?
    title: <Trans i18nKey="business:ReducePage.projections.cards.card4.title" />,
    body: [
      // @i18n: The future periods are reporting periods at least 24-months away.
      <Trans i18nKey="business:ReducePage.projections.cards.card4.body1" />,
      // @i18n: They represent where your business is aiming to get to on its carbon reduction journey.
      <Trans i18nKey="business:ReducePage.projections.cards.card4.body2" />,
    ],
  },
];
export const reductionInstructions = [
  'business:ReducePage.reductions.reducePlan.tip1',
  'business:ReducePage.reductions.reducePlan.tip2',
];
export const barGraphLayoutIsVerticallyScalable = true;
